// services/priceIntelligenceService.js
// ─────────────────────────────────────────────────────────────────────────────
// SCM Price Intelligence Engine
//
// Turns raw tender close-out records into decision-grade market intelligence:
//   • Awarded rates by category & year
//   • Category inflation via a matched-model chained price index (CPI method)
//   • Cost savings achieved, by year and category
//   • Historical price movement per commodity line
//   • Should-cost estimation with escalation to a future award date
//   • Live market-position assessment + negotiation guidance for a quoted rate
//   • Supplier competitiveness scorecards
//   • Budget projection for a forward year
//
// DESIGN NOTE — comparability:
//   Rates are only ever compared within the same
//   (category, commodity group, unit of measure, currency) tuple.
//   Comparing "NGN per trip" against "USD per MT" is meaningless, so every
//   statistic in this module is computed inside a comparable "line" first and
//   only then aggregated upward. This is what keeps the intelligence honest.
// ─────────────────────────────────────────────────────────────────────────────

// ── Derivation cache ─────────────────────────────────────────────────────────
//
// The heavy derivations here are pure functions of the record array, and the UI
// calls them repeatedly: rendering the Products tab alone asks for
// buildProductIntelligence five times over, and shouldCost recomputes the whole
// inflation index on every keystroke in the live rate check.
//
// Results are cached against the identity of the array passed in. React hands
// back the same array reference until the data actually changes, and
// filterByDateRange returns the input untouched when no window is set, so
// identity is a sound cache key. A WeakMap means a superseded record set is
// collected as soon as nothing else holds it.

const _derived = new WeakMap();

function memoise(records, key, compute) {
  if (!records || typeof records !== "object") return compute();
  let bucket = _derived.get(records);
  if (!bucket) { bucket = Object.create(null); _derived.set(records, bucket); }
  if (!(key in bucket)) bucket[key] = compute();
  return bucket[key];
}

// ── Statistical primitives ───────────────────────────────────────────────────

export function mean(xs) {
  const v = xs.filter(isFinite);
  if (!v.length) return null;
  return v.reduce((s, x) => s + x, 0) / v.length;
}

export function median(xs) {
  return percentile(xs, 50);
}

/** Linear-interpolated percentile (same method as Excel PERCENTILE.INC). */
export function percentile(xs, p) {
  const v = xs.filter(isFinite).sort((a, b) => a - b);
  if (!v.length) return null;
  if (v.length === 1) return v[0];
  const rank = (p / 100) * (v.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return v[lo];
  return v[lo] + (v[hi] - v[lo]) * (rank - lo);
}

export function stdDev(xs) {
  const v = xs.filter(isFinite);
  if (v.length < 2) return null;
  const m = mean(v);
  return Math.sqrt(v.reduce((s, x) => s + (x - m) ** 2, 0) / (v.length - 1));
}

/** Coefficient of variation — unitless dispersion. The volatility measure. */
export function coeffVariation(xs) {
  const m = mean(xs);
  const s = stdDev(xs);
  if (m === null || s === null || m === 0) return null;
  return s / Math.abs(m);
}

/** Geometric mean — the correct average for ratios / growth factors. */
export function geoMean(xs) {
  const v = xs.filter((x) => isFinite(x) && x > 0);
  if (!v.length) return null;
  return Math.exp(v.reduce((s, x) => s + Math.log(x), 0) / v.length);
}

/** Least-squares fit. Returns slope, intercept and r² (fit quality). */
export function linearRegression(points) {
  const p = points.filter((d) => isFinite(d.x) && isFinite(d.y));
  const n = p.length;
  if (n < 2) return null;
  const mx = mean(p.map((d) => d.x));
  const my = mean(p.map((d) => d.y));
  let num = 0, den = 0;
  p.forEach((d) => { num += (d.x - mx) * (d.y - my); den += (d.x - mx) ** 2; });
  if (den === 0) return null;
  const slope = num / den;
  const intercept = my - slope * mx;
  const ssTot = p.reduce((s, d) => s + (d.y - my) ** 2, 0);
  const ssRes = p.reduce((s, d) => s + (d.y - (slope * d.x + intercept)) ** 2, 0);
  return { slope, intercept, r2: ssTot === 0 ? 1 : 1 - ssRes / ssTot, n };
}

/** Compound annual growth rate between two values over a number of years. */
export function cagr(startValue, endValue, years) {
  if (!startValue || startValue <= 0 || !endValue || endValue <= 0 || years <= 0) return null;
  return (endValue / startValue) ** (1 / years) - 1;
}

// ── Record helpers ───────────────────────────────────────────────────────────

/** Records usable for rate analysis: an awarded rate and a tender date. */
export function usableRecords(records) {
  return memoise(records, "usable", () =>
    (records || []).filter((r) => r.awardedRate > 0 && r.tenderDate));
}

export function recordYear(r) {
  return r.tenderDate ? r.tenderDate.slice(0, 4) : null;
}

/**
 * Identity of a comparable commodity line. Everything in this module rolls up
 * from this key — it is what makes rate comparison valid.
 */
export function commodityKey(r) {
  const group = (r.commodityGroup || r.serviceDescription || "").trim().toLowerCase();
  return [
    (r.category || "Other").trim(),
    group.slice(0, 60),
    (r.unitOfMeasure || "unspecified").trim().toLowerCase(),
    (r.currency || "NGN").trim().toUpperCase(),
  ].join(" | ");
}

/** Human-readable label for a commodity key. */
export function commodityLabel(r) {
  const group = r.commodityGroup || r.serviceDescription || "Unspecified";
  const unit = r.unitOfMeasure ? ` · ${r.unitOfMeasure}` : "";
  return `${group.length > 48 ? group.slice(0, 47) + "…" : group}${unit}`;
}

/** Group records into comparable commodity lines, newest award first. */
export function buildCommodityLines(records) {
  return memoise(records, "commodityLines", () => _buildCommodityLines(records));
}

function _buildCommodityLines(records) {
  const map = {};
  usableRecords(records).forEach((r) => {
    const key = commodityKey(r);
    if (!map[key]) {
      map[key] = {
        key,
        label: commodityLabel(r),
        category: r.category || "Other",
        commodityGroup: r.commodityGroup || r.serviceDescription || "Unspecified",
        unitOfMeasure: r.unitOfMeasure || "",
        currency: r.currency || "NGN",
        records: [],
      };
    }
    map[key].records.push(r);
  });

  return Object.values(map).map((line) => {
    const sorted = [...line.records].sort(
      (a, b) => new Date(a.tenderDate) - new Date(b.tenderDate),
    );
    const rates = sorted.map((r) => r.awardedRate);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const spanYears =
      (new Date(last.tenderDate) - new Date(first.tenderDate)) / (365.25 * 86400000);

    return {
      ...line,
      records: sorted,
      n: sorted.length,
      firstAward: first,
      latestAward: last,
      latestRate: last.awardedRate,
      min: Math.min(...rates),
      max: Math.max(...rates),
      mean: mean(rates),
      median: median(rates),
      p25: percentile(rates, 25),
      p75: percentile(rates, 75),
      volatility: coeffVariation(rates),
      spanYears,
      cagr: spanYears >= 0.5 ? cagr(first.awardedRate, last.awardedRate, spanYears) : null,
      suppliers: [...new Set(sorted.map((r) => r.supplierName).filter(Boolean))],
    };
  }).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
}

// ── 1. Awarded rates by category & year ──────────────────────────────────────

/**
 * Pivot of awarded rates by category and year.
 * Reports median as the headline (resistant to one-off outlier awards) with
 * mean, spread and record count alongside.
 */
export function awardedRatesByCategoryYear(records) {
  return memoise(records, "ratesMatrix", () => _awardedRatesByCategoryYear(records));
}

function _awardedRatesByCategoryYear(records) {
  const usable = usableRecords(records);
  const years = [...new Set(usable.map(recordYear).filter(Boolean))].sort();
  const categories = [...new Set(usable.map((r) => r.category || "Other"))].sort();

  const rows = categories.map((category) => {
    const cells = {};
    years.forEach((year) => {
      const subset = usable.filter(
        (r) => (r.category || "Other") === category && recordYear(r) === year,
      );
      if (!subset.length) { cells[year] = null; return; }
      const rates = subset.map((r) => r.awardedRate);
      const savings = subset.reduce((s, r) => s + (r.costSavingAchieved || 0), 0);
      cells[year] = {
        n: subset.length,
        median: median(rates),
        mean: mean(rates),
        min: Math.min(...rates),
        max: Math.max(...rates),
        volatility: coeffVariation(rates),
        savings,
        currency: subset[0].currency || "NGN",
        mixedCurrency: new Set(subset.map((r) => r.currency || "NGN")).size > 1,
        mixedUnits: new Set(subset.map((r) => r.unitOfMeasure || "")).size > 1,
      };
    });
    const totalN = years.reduce((s, y) => s + (cells[y]?.n || 0), 0);
    return { category, cells, totalN };
  }).filter((r) => r.totalN > 0);

  return { years, rows };
}

// ── 2. Inflation trend by category & year ────────────────────────────────────

/**
 * Matched-model chained price index — the same methodology national statistics
 * offices use to compute CPI.
 *
 * For each consecutive year pair we take only the commodity lines that were
 * awarded in BOTH years, compute each line's price relative (rate_Y / rate_Y-1),
 * and take the geometric mean of those relatives. That measures pure price
 * movement, immune to mix shift — a year that happened to tender more expensive
 * commodities does not masquerade as inflation.
 *
 * Where no matched pairs exist we fall back to the change in median rate and
 * mark the result `matched: false` so the UI can flag it as indicative only.
 */
export function inflationByCategoryYear(records) {
  return memoise(records, "inflation", () => _inflationByCategoryYear(records));
}

function _inflationByCategoryYear(records) {
  const usable = usableRecords(records);
  const years = [...new Set(usable.map(recordYear).filter(Boolean))].sort();
  const categories = [...new Set(usable.map((r) => r.category || "Other"))].sort();

  // Median rate per (commodity line, year) — the comparison unit.
  const lineYearRate = {};
  usable.forEach((r) => {
    const k = `${commodityKey(r)}@@${recordYear(r)}`;
    (lineYearRate[k] = lineYearRate[k] || []).push(r.awardedRate);
  });
  Object.keys(lineYearRate).forEach((k) => {
    lineYearRate[k] = median(lineYearRate[k]);
  });

  const rows = categories.map((category) => {
    const catRecords = usable.filter((r) => (r.category || "Other") === category);
    const catLines = [...new Set(catRecords.map(commodityKey))];
    const cells = {};
    let index = 100;
    let baseYear = null;

    years.forEach((year, i) => {
      const inYear = catRecords.filter((r) => recordYear(r) === year);
      if (!inYear.length) { cells[year] = null; return; }

      if (baseYear === null) {
        baseYear = year;
        cells[year] = { inflation: null, index: 100, matched: true, pairs: 0, base: true, n: inYear.length };
        return;
      }

      const prevYear = years[i - 1];
      const relatives = [];
      catLines.forEach((line) => {
        const now = lineYearRate[`${line}@@${year}`];
        const before = lineYearRate[`${line}@@${prevYear}`];
        if (now > 0 && before > 0) relatives.push(now / before);
      });

      let inflation = null;
      let matched = true;

      if (relatives.length > 0) {
        inflation = geoMean(relatives) - 1;
      } else {
        // No like-for-like line. Fall back to median shift, clearly flagged.
        const nowMed = median(inYear.map((r) => r.awardedRate));
        const prevSet = catRecords.filter((r) => recordYear(r) === prevYear);
        const prevMed = prevSet.length ? median(prevSet.map((r) => r.awardedRate)) : null;
        if (nowMed > 0 && prevMed > 0) { inflation = nowMed / prevMed - 1; matched = false; }
      }

      if (inflation !== null) index = index * (1 + inflation);
      cells[year] = {
        inflation,
        index: inflation !== null ? index : null,
        matched,
        pairs: relatives.length,
        n: inYear.length,
      };
    });

    const withInf = years.map((y) => cells[y]).filter((c) => c && c.inflation !== null);
    const avgInflation = withInf.length
      ? geoMean(withInf.map((c) => 1 + c.inflation)) - 1
      : null;

    return {
      category,
      cells,
      avgInflation,
      matchedPairs: withInf.reduce((s, c) => s + c.pairs, 0),
      confidence: confidenceOf(
        withInf.reduce((s, c) => s + c.pairs, 0),
        withInf.some((c) => !c.matched) ? 0.6 : 0.2,
      ),
    };
  }).filter((r) => years.some((y) => r.cells[y]));

  // Portfolio-wide inflation: geometric mean across categories, weighted by records.
  const overall = {};
  years.forEach((year) => {
    const cells = rows.map((r) => r.cells[year]).filter((c) => c && c.inflation !== null);
    overall[year] = cells.length ? geoMean(cells.map((c) => 1 + c.inflation)) - 1 : null;
  });

  return { years, rows, overall };
}

// ── 3. Cost savings achieved ─────────────────────────────────────────────────

/**
 * Savings, by year and category. Two figures are reported because they answer
 * different questions:
 *   • declared — what the buyer recorded in the close-out form
 *   • derived  — (benchmark − awarded), the saving implied by the rates
 * A large gap between them means close-out forms are being under-reported.
 */
export function costSavings(records, focusYear) {
  const usable = usableRecords(records);
  const years = [...new Set(usable.map(recordYear).filter(Boolean))].sort();

  const byYear = years.map((year) => {
    const subset = usable.filter((r) => recordYear(r) === year);
    const declared = subset.reduce((s, r) => s + (r.costSavingAchieved || 0), 0);
    const withBench = subset.filter((r) => r.benchmarkRate > 0);
    const derived = withBench.reduce(
      (s, r) => s + Math.max(0, r.benchmarkRate - r.awardedRate),
      0,
    );
    const reductions = withBench.filter((r) => r.awardedRate < r.benchmarkRate);
    return {
      year,
      n: subset.length,
      declared,
      derived,
      benchmarked: withBench.length,
      reductions: reductions.length,
      reductionRate: withBench.length ? reductions.length / withBench.length : null,
      avgPctChange: withBench.length
        ? mean(withBench.map((r) => r.percentageChange || 0))
        : null,
      currency: subset[0]?.currency || "NGN",
    };
  });

  const focus = focusYear ? byYear.find((y) => y.year === String(focusYear)) : null;

  const byCategory = [...new Set(usable.map((r) => r.category || "Other"))]
    .map((category) => {
      const subset = usable.filter(
        (r) => (r.category || "Other") === category &&
          (!focusYear || recordYear(r) === String(focusYear)),
      );
      if (!subset.length) return null;
      const withBench = subset.filter((r) => r.benchmarkRate > 0);
      return {
        category,
        n: subset.length,
        declared: subset.reduce((s, r) => s + (r.costSavingAchieved || 0), 0),
        derived: withBench.reduce((s, r) => s + Math.max(0, r.benchmarkRate - r.awardedRate), 0),
        avgPctChange: withBench.length ? mean(withBench.map((r) => r.percentageChange || 0)) : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (b.declared + b.derived) - (a.declared + a.derived));

  return { years, byYear, byCategory, focus };
}

// ── 4. Historical price movement ─────────────────────────────────────────────

/**
 * Award-by-award price history for one commodity line, with the step change
 * between consecutive awards and a fitted trend.
 */
export function priceMovement(records, key) {
  const line = buildCommodityLines(records).find((l) => l.key === key);
  if (!line) return null;

  const series = line.records.map((r, i) => {
    const prev = i > 0 ? line.records[i - 1] : null;
    return {
      date: r.tenderDate,
      year: recordYear(r),
      rate: r.awardedRate,
      supplier: r.supplierName,
      tenderRef: r.tenderRef,
      benchmarkRate: r.benchmarkRate || null,
      changeFromPrev: prev ? (r.awardedRate / prev.awardedRate - 1) : null,
      t: new Date(r.tenderDate).getTime(),
    };
  });

  const t0 = series[0].t;
  const fit = linearRegression(
    series.map((p) => ({ x: (p.t - t0) / (365.25 * 86400000), y: p.rate })),
  );

  return {
    line,
    series,
    fit,
    trendPerYear: fit ? fit.slope : null,
    trendPctPerYear: fit && line.mean ? fit.slope / line.mean : null,
  };
}

/** Every commodity line that has enough history to plot a movement. */
export function trackableCommodities(records, minPoints = 2) {
  return buildCommodityLines(records).filter((l) => l.n >= minPoints);
}

// ── Confidence scoring ───────────────────────────────────────────────────────

/**
 * Every estimate this engine produces carries a confidence grade, driven by
 * sample size and price dispersion. SCM should negotiate differently on a
 * "High" estimate than on one built from two noisy data points.
 */
export function confidenceOf(n, volatility) {
  const v = volatility === null || volatility === undefined ? 0.3 : volatility;
  if (n >= 8 && v < 0.30) return { level: "High",   score: 4, note: `${n} comparable awards, stable pricing` };
  if (n >= 5 && v < 0.50) return { level: "Good",   score: 3, note: `${n} comparable awards` };
  if (n >= 3)             return { level: "Fair",   score: 2, note: `${n} awards — treat as indicative` };
  if (n >= 1)             return { level: "Low",    score: 1, note: `only ${n} comparable award${n > 1 ? "s" : ""}` };
  return { level: "None", score: 0, note: "no comparable history" };
}

// ── 5. Should-cost estimation ────────────────────────────────────────────────

/**
 * What should this cost?
 *
 * Builds a fair-value range from historical awards on the matching commodity
 * line, then escalates it to the target award date using that category's
 * measured inflation rate. Falls back progressively:
 *   exact commodity line → same commodity group → whole category
 * so SCM always gets a usable answer, with the confidence grade reflecting how
 * far it had to fall back.
 */
export function shouldCost(records, query) {
  const {
    category, commodityGroup, unitOfMeasure, currency = "NGN",
    targetDate, location,
  } = query;

  const allUsable = usableRecords(records);

  const norm = (s) => (s || "").trim().toLowerCase();
  const sameUnit = (r) => norm(r.unitOfMeasure) === norm(unitOfMeasure);
  const sameGroup = (r) => norm(r.commodityGroup) === norm(commodityGroup) && norm(commodityGroup) !== "";
  const sameCat = (r) => (r.category || "Other") === category;
  const sameCurrency = (r) => (r.currency || "NGN").toUpperCase() === currency.toUpperCase();

  let basis = null;
  let matchLevel = null;
  let productScoped = false;
  let warning = null;

  if (commodityGroup) {
    // A specific product was named. The answer must be about THAT product —
    // never a different one from the same category. Where the named product has
    // no usable history the honest answer is to say so and explain what is
    // missing, not to quietly substitute an unrelated line.
    const inCategory = allUsable.filter((r) => sameCat(r) && sameGroup(r));

    if (!inCategory.length) {
      const elsewhere = allUsable.filter(sameGroup);
      return {
        available: false,
        productScoped: true,
        reason: elsewhere.length
          ? `"${commodityGroup}" has no award history under ${category}. It appears ${elsewhere.length} time${elsewhere.length === 1 ? "" : "s"} under ${[...new Set(elsewhere.map((r) => r.category))].join(", ")} — select that category instead.`
          : `No award history yet for "${commodityGroup}". This will be the first close-out on this product line.`,
        suggestCategory: elsewhere.length ? elsewhere[0].category : null,
        confidence: confidenceOf(0, null),
      };
    }

    const inCurrency = inCategory.filter(sameCurrency);
    if (!inCurrency.length) {
      const curs = [...new Set(inCategory.map((r) => (r.currency || "NGN").toUpperCase()))];
      return {
        available: false,
        productScoped: true,
        reason: `"${commodityGroup}" has ${inCategory.length} award${inCategory.length === 1 ? "" : "s"} on record, but priced in ${curs.join(" or ")} — not ${currency}. Rates in different currencies cannot be compared directly.`,
        suggestCurrency: curs[0],
        confidence: confidenceOf(0, null),
      };
    }

    if (unitOfMeasure) {
      const exact = inCurrency.filter(sameUnit);
      if (!exact.length) {
        const units = [...new Set(inCurrency.map((r) => r.unitOfMeasure).filter(Boolean))];
        return {
          available: false,
          productScoped: true,
          reason: units.length
            ? `"${commodityGroup}" is priced ${units.map((u) => `per ${u.toLowerCase()}`).join(" or ")} on record, not per ${unitOfMeasure.toLowerCase()}. A rate per one unit says nothing about the rate per another.`
            : `No unit of measure was recorded against the "${commodityGroup}" awards, so they cannot be matched to "${unitOfMeasure}".`,
          suggestUnit: units[0] || null,
          confidence: confidenceOf(0, null),
        };
      }
      basis = exact;
      matchLevel = `${commodityGroup} · per ${unitOfMeasure.toLowerCase()} · ${currency}`;
    } else {
      const units = [...new Set(inCurrency.map((r) => r.unitOfMeasure).filter(Boolean))];
      basis = inCurrency;
      matchLevel = units.length === 1
        ? `${commodityGroup} · per ${units[0].toLowerCase()} · ${currency}`
        : `${commodityGroup} · ${currency}`;
      if (units.length > 1) {
        warning = `These awards mix ${units.length} units of measure (${units.join(", ")}). Pick one to get a comparable range.`;
      }
    }
    productScoped = true;
  } else {
    // No product named — a category-level view is what was asked for, and a
    // category-wide basis is the honest answer to that question.
    const inCategory = allUsable.filter((r) => sameCat(r) && sameCurrency(r));
    if (!inCategory.length) {
      return {
        available: false,
        productScoped: false,
        reason: `No ${currency} award history under ${category} yet.`,
        confidence: confidenceOf(0, null),
      };
    }
    if (unitOfMeasure) {
      const cu = inCategory.filter(sameUnit);
      if (cu.length) {
        basis = cu;
        matchLevel = `All ${category} priced per ${unitOfMeasure.toLowerCase()} · ${currency}`;
        const groups = [...new Set(cu.map((r) => r.commodityGroup).filter(Boolean))];
        if (groups.length > 1) {
          warning = `Spans ${groups.length} different products (${groups.slice(0, 4).join(", ")}${groups.length > 4 ? "…" : ""}). Name a commodity group for a product-specific estimate.`;
        }
      }
    }
    if (!basis) {
      basis = inCategory;
      matchLevel = `Category-wide across all ${category} products · ${currency}`;
      const groups = [...new Set(inCategory.map((r) => r.commodityGroup).filter(Boolean))];
      warning = groups.length > 1
        ? `This blends ${groups.length} unrelated products (${groups.slice(0, 4).join(", ")}${groups.length > 4 ? "…" : ""}) priced on different units. Treat it as a rough order of magnitude only — select a commodity group for a real estimate.`
        : "No commodity group selected, so this is a category-wide figure rather than a product estimate.";
    }
  }

  const sorted = [...basis].sort((a, b) => new Date(b.tenderDate) - new Date(a.tenderDate));
  const rates = basis.map((r) => r.awardedRate);
  const vol = coeffVariation(rates);

  // Escalate from the most recent comparable award to the target award date.
  const inflation = inflationByCategoryYear(records);
  const catInf = inflation.rows.find((r) => r.category === category);
  const escalationRate =
    catInf?.avgInflation !== null && catInf?.avgInflation !== undefined
      ? catInf.avgInflation
      : (inflation.overall[inflation.years[inflation.years.length - 1]] ?? 0);

  const latest = sorted[0];
  const target = targetDate ? new Date(targetDate) : new Date();
  const yearsOut = Math.max(
    0,
    (target - new Date(latest.tenderDate)) / (365.25 * 86400000),
  );
  const factor = (1 + (escalationRate || 0)) ** yearsOut;

  const base = {
    p25: percentile(rates, 25),
    median: median(rates),
    p75: percentile(rates, 75),
    min: Math.min(...rates),
    max: Math.max(...rates),
    latestRate: latest.awardedRate,
  };

  const locationMatches = location
    ? basis.filter((r) => norm(r.location) === norm(location))
    : [];

  return {
    available: true,
    matchLevel,
    productScoped,
    warning,
    currency,
    n: basis.length,
    basis: sorted.slice(0, 12),
    suppliers: [...new Set(basis.map((r) => r.supplierName).filter(Boolean))],
    volatility: vol,
    confidence: confidenceOf(basis.length, vol),
    base,
    escalation: {
      rate: escalationRate,
      yearsOut,
      factor,
      fromDate: latest.tenderDate,
      toDate: targetDate || new Date().toISOString().slice(0, 10),
    },
    estimate: {
      low: base.p25 * factor,
      target: base.median * factor,
      high: base.p75 * factor,
      ceiling: base.max * factor,
    },
    locationNote: locationMatches.length
      ? `${locationMatches.length} of these awards were in ${location} (median ${median(locationMatches.map((r) => r.awardedRate))?.toFixed(2)})`
      : null,
  };
}

/**
 * What actually exists in the database, indexed for the should-cost form:
 *   category → commodity group → the units and currencies it has been bought in.
 *
 * Driving the dropdowns from this is what stops a buyer asking for VSAT priced
 * in naira per unit, a combination that has never been tendered, and getting a
 * confident-looking number back that was really about something else.
 */
export function productCatalogue(records) {
  return memoise(records, "catalogue", () => _productCatalogue(records));
}

function _productCatalogue(records) {
  const byCategory = {};

  usableRecords(records).forEach((r) => {
    const cat = r.category || "Other";
    const group = (r.commodityGroup || "").trim();
    if (!group) return;

    byCategory[cat] = byCategory[cat] || {};
    const entry = (byCategory[cat][group] = byCategory[cat][group] || {
      group, category: cat, units: new Set(), currencies: new Set(),
      locations: new Set(), n: 0, lastDate: "",
    });
    entry.n++;
    if (r.unitOfMeasure) entry.units.add(r.unitOfMeasure);
    entry.currencies.add((r.currency || "NGN").toUpperCase());
    if (r.location) entry.locations.add(r.location);
    if (r.tenderDate > entry.lastDate) entry.lastDate = r.tenderDate;
  });

  const out = {};
  Object.entries(byCategory).forEach(([cat, groups]) => {
    out[cat] = Object.values(groups)
      .map((g) => ({
        group: g.group,
        category: g.category,
        n: g.n,
        lastDate: g.lastDate,
        units: [...g.units].sort(),
        currencies: [...g.currencies].sort(),
        locations: [...g.locations].sort(),
      }))
      .sort((a, b) => b.n - a.n || a.group.localeCompare(b.group));
  });
  return out;
}

// ── 6. Live rate assessment + negotiation guidance ───────────────────────────

/**
 * Score a quoted rate against market history and produce a negotiating position.
 * This is what fires inside the close-out form as a buyer types the awarded rate.
 */
export function assessRate(records, query, quotedRate) {
  const sc = shouldCost(records, query);
  if (!sc.available || !quotedRate || quotedRate <= 0) {
    return { available: false, shouldCost: sc };
  }

  const allRates = sc.basis.map((r) => r.awardedRate);

  // Percentile rank of the quote within escalated history.
  const escalated = allRates.map((x) => x * sc.escalation.factor).sort((a, b) => a - b);
  const below = escalated.filter((x) => x < quotedRate).length;
  const pctRank = escalated.length ? (below / escalated.length) * 100 : null;

  const vsTarget = sc.estimate.target > 0 ? quotedRate / sc.estimate.target - 1 : null;

  let verdict, tone, headline;
  if (vsTarget === null) {
    verdict = "Unscored"; tone = "neutral"; headline = "Not enough history to score this rate.";
  } else if (vsTarget <= -0.15) {
    verdict = "Excellent"; tone = "good";
    headline = `${fmtPct(Math.abs(vsTarget))} below the market target rate — a strong award.`;
  } else if (vsTarget <= -0.05) {
    verdict = "Competitive"; tone = "good";
    headline = `${fmtPct(Math.abs(vsTarget))} below target — comfortably within market.`;
  } else if (vsTarget < 0.05) {
    verdict = "At market"; tone = "neutral";
    headline = "In line with the market target rate for this commodity.";
  } else if (vsTarget < 0.15) {
    verdict = "Above market"; tone = "warn";
    headline = `${fmtPct(vsTarget)} above target — there is room to push back.`;
  } else {
    verdict = "Well above market"; tone = "bad";
    headline = `${fmtPct(vsTarget)} above the market target — challenge this rate before award.`;
  }

  // Negotiation levers, drawn from what the data actually shows.
  const levers = [];
  if (sc.volatility !== null && sc.volatility > 0.35) {
    levers.push(`Rates on this line vary by ±${fmtPct(sc.volatility)} — wide dispersion means the price is negotiable.`);
  } else if (sc.volatility !== null && sc.volatility < 0.12) {
    levers.push("Rates on this line are tightly clustered — expect limited movement on price; negotiate on terms instead.");
  }
  if (sc.suppliers.length >= 3) {
    levers.push(`${sc.suppliers.length} suppliers have won this line before — credible competitive tension exists.`);
  } else if (sc.suppliers.length === 1) {
    levers.push(`Only ${sc.suppliers[0]} has supplied this line — single-source exposure weakens our position. Widen the bidder list.`);
  }
  if (sc.escalation.rate > 0.10) {
    levers.push(`Category is running at ${fmtPct(sc.escalation.rate)} annual inflation — push for a longer fixed-price term to lock today's rate.`);
  } else if (sc.escalation.rate < 0) {
    levers.push(`Category prices are falling (${fmtPct(sc.escalation.rate)}/yr) — resist any escalation clause and keep the term short.`);
  }
  if (quotedRate > sc.base.max * sc.escalation.factor) {
    levers.push("This quote is above every comparable award on record — ask the supplier to justify the premium in writing.");
  }
  if (quotedRate < sc.base.min * sc.escalation.factor) {
    levers.push("This quote is below every comparable award on record — verify scope and specification before award to rule out an underbid.");
  }

  return {
    available: true,
    shouldCost: sc,
    quotedRate,
    verdict,
    tone,
    headline,
    vsTarget,
    vsLow: sc.estimate.low > 0 ? quotedRate / sc.estimate.low - 1 : null,
    percentileRank: pctRank,
    savingVsTarget: sc.estimate.target - quotedRate,
    negotiation: {
      target: sc.estimate.target,
      stretch: sc.estimate.low,
      walkAway: sc.estimate.high,
      levers,
    },
  };
}

// ── 7. Supplier competitiveness ──────────────────────────────────────────────

/**
 * Rank suppliers by how their awarded rates compare to the median rate on the
 * same commodity lines. Index 100 = exactly at market; below 100 = cheaper.
 */
export function supplierScorecard(records) {
  return memoise(records, "suppliers", () => _supplierScorecard(records));
}

function _supplierScorecard(records) {
  const lines = buildCommodityLines(records);
  const lineMedian = {};
  lines.forEach((l) => { lineMedian[l.key] = l.median; });

  const map = {};
  usableRecords(records).forEach((r) => {
    const name = r.supplierName || "Unnamed supplier";
    if (!map[name]) {
      map[name] = {
        supplier: name, awards: 0, categories: new Set(),
        lines: new Set(), relatives: [], savings: 0, latest: null, pctChanges: [],
      };
    }
    const s = map[name];
    s.awards++;
    s.categories.add(r.category || "Other");
    s.lines.add(commodityKey(r));
    s.savings += r.costSavingAchieved || 0;
    if (r.benchmarkRate > 0) s.pctChanges.push(r.percentageChange || 0);
    const med = lineMedian[commodityKey(r)];
    if (med > 0) s.relatives.push(r.awardedRate / med);
    if (!s.latest || new Date(r.tenderDate) > new Date(s.latest)) s.latest = r.tenderDate;
  });

  return Object.values(map).map((s) => {
    const rel = geoMean(s.relatives);
    return {
      supplier: s.supplier,
      awards: s.awards,
      categories: [...s.categories],
      lineCount: s.lines.size,
      priceIndex: rel !== null ? rel * 100 : null,
      declaredSavings: s.savings,
      avgPctChange: s.pctChanges.length ? mean(s.pctChanges) : null,
      latestAward: s.latest,
      comparable: s.relatives.length,
    };
  }).sort((a, b) => b.awards - a.awards);
}

// ── 8. Budget projection ─────────────────────────────────────────────────────

/**
 * Project next year's rates per category by applying measured category
 * inflation to the current median award. Feeds the annual budgeting exercise.
 */
export function budgetProjection(records, targetYear) {
  const usable = usableRecords(records);
  const inflation = inflationByCategoryYear(records);
  const years = [...new Set(usable.map(recordYear).filter(Boolean))].sort();
  const latestYear = years[years.length - 1];
  if (!latestYear) return { available: false, rows: [], targetYear };

  const yearsOut = Math.max(0, Number(targetYear) - Number(latestYear));

  const rows = [...new Set(usable.map((r) => r.category || "Other"))].map((category) => {
    const subset = usable.filter((r) => (r.category || "Other") === category);
    const recent = subset.filter((r) => recordYear(r) === latestYear);
    const basis = recent.length ? recent : subset;
    const rates = basis.map((r) => r.awardedRate);
    const catInf = inflation.rows.find((r) => r.category === category);
    const rate = catInf?.avgInflation ?? inflation.overall[latestYear] ?? 0;
    const factor = (1 + (rate || 0)) ** yearsOut;
    const med = median(rates);
    const vol = coeffVariation(rates);

    return {
      category,
      n: basis.length,
      basisYear: recent.length ? latestYear : "all years",
      currentMedian: med,
      inflationRate: rate,
      projectedMedian: med !== null ? med * factor : null,
      projectedLow: med !== null ? percentile(rates, 25) * factor : null,
      projectedHigh: med !== null ? percentile(rates, 75) * factor : null,
      uplift: med !== null && med > 0 ? factor - 1 : null,
      currency: basis[0]?.currency || "NGN",
      confidence: confidenceOf(basis.length, vol),
    };
  }).filter((r) => r.currentMedian !== null)
    .sort((a, b) => (b.inflationRate ?? 0) - (a.inflationRate ?? 0));

  return { available: rows.length > 0, rows, targetYear, latestYear, yearsOut };
}

// ── 9. Automated insights ────────────────────────────────────────────────────

/**
 * Reads the whole dataset and writes the headlines a category manager would
 * want on a briefing note: what is inflating, what is volatile, where we are
 * single-sourced, and where the data itself is too thin to trust.
 */
export function generateInsights(records) {
  const usable = usableRecords(records);
  const out = [];
  if (usable.length < 3) {
    return [{
      severity: "info",
      title: "Building the evidence base",
      body: `${usable.length} usable record${usable.length === 1 ? "" : "s"} so far. Price intelligence becomes reliable from roughly 20 close-outs across a category.`,
    }];
  }

  const inflation = inflationByCategoryYear(records);
  const lines = buildCommodityLines(records);
  const suppliers = supplierScorecard(records);
  const years = inflation.years;
  const latestYear = years[years.length - 1];

  // Fastest-inflating categories
  const inflating = inflation.rows
    .filter((r) => r.avgInflation !== null && r.avgInflation > 0.05)
    .sort((a, b) => b.avgInflation - a.avgInflation)
    .slice(0, 3);
  inflating.forEach((r) => {
    out.push({
      severity: r.avgInflation > 0.20 ? "critical" : "warn",
      title: `${r.category} rising at ${fmtPct(r.avgInflation)} a year`,
      body: `Measured across ${r.matchedPairs} like-for-like commodity comparison${r.matchedPairs === 1 ? "" : "s"} (${r.confidence.level.toLowerCase()} confidence). Build this escalation into ${Number(latestYear) + 1} budgets and press for fixed-price terms on renewals.`,
    });
  });

  // Categories where prices are falling — a buying opportunity
  const deflating = inflation.rows
    .filter((r) => r.avgInflation !== null && r.avgInflation < -0.03)
    .sort((a, b) => a.avgInflation - b.avgInflation)
    .slice(0, 2);
  deflating.forEach((r) => {
    out.push({
      severity: "good",
      title: `${r.category} prices falling ${fmtPct(Math.abs(r.avgInflation))} a year`,
      body: "Favourable buying conditions. Keep contract terms short so we can re-tender into a softening market rather than locking in today's rate.",
    });
  });

  // High-volatility lines — the biggest negotiation opportunities
  const volatile = lines
    .filter((l) => l.n >= 3 && l.volatility !== null && l.volatility > 0.35)
    .sort((a, b) => b.volatility - a.volatility)
    .slice(0, 3);
  volatile.forEach((l) => {
    out.push({
      severity: "warn",
      title: `Wide price spread on ${l.label}`,
      body: `Awarded rates range ${fmtMoney(l.min, l.currency)} to ${fmtMoney(l.max, l.currency)} across ${l.n} awards (±${fmtPct(l.volatility)}). That dispersion is negotiating room — anchor the next tender near ${fmtMoney(l.p25, l.currency)}.`,
    });
  });

  // Single-source exposure on repeat spend
  const singleSourced = lines
    .filter((l) => l.n >= 3 && l.suppliers.length === 1)
    .slice(0, 3);
  singleSourced.forEach((l) => {
    out.push({
      severity: "warn",
      title: `Single-sourced: ${l.label}`,
      body: `All ${l.n} awards went to ${l.suppliers[0]}. Without competitive tension we cannot validate the rate. Widen the bidder list at next renewal.`,
    });
  });

  // Most competitive supplier
  const best = suppliers
    .filter((s) => s.comparable >= 3 && s.priceIndex !== null)
    .sort((a, b) => a.priceIndex - b.priceIndex)[0];
  if (best && best.priceIndex < 95) {
    out.push({
      severity: "good",
      title: `${best.supplier} prices ${fmtPct((100 - best.priceIndex) / 100)} below market`,
      body: `Across ${best.comparable} comparable awards this supplier consistently beats the median rate. Worth inviting to more tenders in ${best.categories.slice(0, 3).join(", ")}.`,
    });
  }

  // Data quality: unusable records
  const noRate = (records || []).length - usable.length;
  if (noRate > 0) {
    out.push({
      severity: "info",
      title: `${noRate} record${noRate === 1 ? "" : "s"} missing an awarded rate or tender date`,
      body: "These are excluded from all price analysis. Completing them will sharpen every estimate in this dashboard.",
    });
  }

  const noBench = usable.filter((r) => !r.benchmarkRate).length;
  if (noBench > usable.length * 0.4) {
    out.push({
      severity: "info",
      title: `${noBench} of ${usable.length} awards have no benchmark rate`,
      body: "Without a benchmark we cannot measure the saving delivered. Capturing the previous or budgeted rate at close-out is the single highest-value field in the form.",
    });
  }

  const thin = inflation.rows.filter((r) => r.confidence.score <= 1);
  if (thin.length) {
    out.push({
      severity: "info",
      title: `Thin history in ${thin.map((r) => r.category).slice(0, 4).join(", ")}`,
      body: "Inflation figures for these categories rest on very few like-for-like comparisons. Treat them as directional until more close-outs land.",
    });
  }

  const order = { critical: 0, warn: 1, good: 2, info: 3 };
  return out.sort((a, b) => order[a.severity] - order[b.severity]);
}

// ── 10. Date range filtering ─────────────────────────────────────────────────

/**
 * Restrict a record set to a tender-date window. Both bounds are inclusive and
 * optional, so this doubles as an open-ended "from" or "to" filter.
 */
export function filterByDateRange(records, from, to) {
  if (!from && !to) return records || [];
  return (records || []).filter((r) => {
    if (!r.tenderDate) return false;
    if (from && r.tenderDate < from) return false;
    if (to && r.tenderDate > to) return false;
    return true;
  });
}

/** Earliest and latest tender dates present, for seeding date pickers. */
export function dateBounds(records) {
  const dates = usableRecords(records).map((r) => r.tenderDate).sort();
  return { min: dates[0] || "", max: dates[dates.length - 1] || "" };
}

/** Distinct award years present, newest first. */
export function availableYears(records) {
  return [...new Set(usableRecords(records).map(recordYear).filter(Boolean))]
    .sort()
    .reverse();
}

// ── 11. Product-level intelligence ───────────────────────────────────────────
//
// A "product" here is a distinct procurable line: the commodity group (falling
// back to the service description where no group was recorded), held together
// with its unit of measure and currency. That triple is the finest grain at
// which two awarded rates are genuinely comparable, which makes it the right
// unit for tracking price shift tender by tender.
//
// Everything the category dimension offers — index, inflation, volatility,
// confidence, supplier read — is reproduced here at product level, plus the
// things that only make sense per product: award-over-award movement, supplier
// concentration, and the market observation trail.

export function productKey(r) {
  return commodityKey(r);
}

export function productName(r) {
  const g = (r.commodityGroup || "").trim();
  if (g) return g;
  const d = (r.serviceDescription || "").trim();
  if (!d) return "Unspecified product";
  return d.length > 64 ? `${d.slice(0, 63)}…` : d;
}

/** Herfindahl index of award share by supplier. 1 = single source. */
function herfindahl(counts, total) {
  if (!total) return null;
  return counts.reduce((s, c) => s + (c / total) ** 2, 0);
}

function concentrationLabel(hhi, supplierCount) {
  if (hhi === null) return { level: "Unknown", note: "no supplier data" };
  if (supplierCount === 1) return { level: "Single source", note: "every award to one supplier" };
  if (hhi >= 0.6) return { level: "Highly concentrated", note: "one supplier dominates awards" };
  if (hhi >= 0.4) return { level: "Concentrated", note: "awards cluster on few suppliers" };
  if (hhi >= 0.25) return { level: "Moderate", note: "reasonable spread of awards" };
  return { level: "Competitive", note: "awards well distributed" };
}

/**
 * Full analytical profile for every product in the record set.
 * Sorted by award count so the products with real history surface first.
 */
export function buildProductIntelligence(records) {
  return memoise(records, "products", () => _buildProductIntelligence(records));
}

function _buildProductIntelligence(records) {
  const usable = usableRecords(records);
  const groups = {};

  usable.forEach((r) => {
    const key = productKey(r);
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  });

  return Object.entries(groups).map(([key, list]) => {
    const history = [...list].sort(
      (a, b) => new Date(a.tenderDate) - new Date(b.tenderDate),
    );
    const first = history[0];
    const last = history[history.length - 1];
    const rates = history.map((r) => r.awardedRate);
    const currency = first.currency || "NGN";

    const spanYears =
      (new Date(last.tenderDate) - new Date(first.tenderDate)) / (365.25 * 86400000);

    // Award-over-award movement, and a rebased index starting at 100.
    let idx = 100;
    const awards = history.map((r, i) => {
      const prev = i > 0 ? history[i - 1] : null;
      const change = prev ? r.awardedRate / prev.awardedRate - 1 : null;
      if (prev) idx = idx * (1 + change);
      return {
        id: r.id,
        tenderRef: r.tenderRef,
        date: r.tenderDate,
        year: recordYear(r),
        t: new Date(r.tenderDate).getTime(),
        rate: r.awardedRate,
        benchmarkRate: r.benchmarkRate || null,
        lowestRate: r.lowestRate || null,
        vsBenchmark: r.benchmarkRate > 0 ? r.awardedRate / r.benchmarkRate - 1 : null,
        supplier: r.supplierName || "Not recorded",
        location: r.location || "",
        duration: r.contractDuration || "",
        terms: r.commercialTerms || "",
        justification: r.awardJustification || "",
        observation: r.marketObservation || "",
        description: r.serviceDescription || "",
        saving: r.costSavingAchieved || 0,
        changeFromPrev: change,
        index: i === 0 ? 100 : idx,
      };
    });

    // Median rate per year, plus the year-on-year shift between them.
    const yearBuckets = {};
    history.forEach((r) => {
      const y = recordYear(r);
      if (!y) return;
      (yearBuckets[y] = yearBuckets[y] || []).push(r.awardedRate);
    });
    const years = Object.keys(yearBuckets).sort();
    const byYear = {};
    years.forEach((y, i) => {
      const med = median(yearBuckets[y]);
      const prevY = i > 0 ? years[i - 1] : null;
      const prevMed = prevY ? median(yearBuckets[prevY]) : null;
      byYear[y] = {
        year: y,
        n: yearBuckets[y].length,
        median: med,
        min: Math.min(...yearBuckets[y]),
        max: Math.max(...yearBuckets[y]),
        change: prevMed > 0 ? med / prevMed - 1 : null,
      };
    });

    // Supplier breakdown, ranked by how they price against the product median.
    const med = median(rates);
    const supMap = {};
    history.forEach((r) => {
      const name = r.supplierName || "Not recorded";
      if (!supMap[name]) supMap[name] = { name, awards: 0, rates: [], savings: 0, latest: null };
      supMap[name].awards++;
      supMap[name].rates.push(r.awardedRate);
      supMap[name].savings += r.costSavingAchieved || 0;
      if (!supMap[name].latest || r.tenderDate > supMap[name].latest) {
        supMap[name].latest = r.tenderDate;
      }
    });
    const suppliers = Object.values(supMap).map((s) => ({
      name: s.name,
      awards: s.awards,
      share: s.awards / history.length,
      avgRate: mean(s.rates),
      bestRate: Math.min(...s.rates),
      relIndex: med > 0 ? (mean(s.rates) / med) * 100 : null,
      savings: s.savings,
      latest: s.latest,
    })).sort((a, b) => b.awards - a.awards || a.avgRate - b.avgRate);

    const hhi = herfindahl(suppliers.map((s) => s.awards), history.length);

    const fit = linearRegression(
      awards.map((a) => ({ x: (a.t - awards[0].t) / (365.25 * 86400000), y: a.rate })),
    );

    const benchmarked = history.filter((r) => r.benchmarkRate > 0);
    const belowBenchmark = benchmarked.filter((r) => r.awardedRate < r.benchmarkRate);
    const vol = coeffVariation(rates);

    const changes = awards.map((a) => a.changeFromPrev).filter((c) => c !== null);

    return {
      key,
      name: productName(first),
      category: first.category || "Other",
      unitOfMeasure: first.unitOfMeasure || "",
      currency,
      phase: first.phase || 3,

      descriptions: [...new Set(history.map((r) => r.serviceDescription).filter(Boolean))],
      locations: [...new Set(history.map((r) => r.location).filter(Boolean))],

      n: history.length,
      firstDate: first.tenderDate,
      lastDate: last.tenderDate,
      spanYears,
      years,

      firstRate: first.awardedRate,
      latestRate: last.awardedRate,
      previousRate: history.length > 1 ? history[history.length - 2].awardedRate : null,
      latestChange: awards[awards.length - 1].changeFromPrev,
      totalChange: history.length > 1 ? last.awardedRate / first.awardedRate - 1 : null,
      cagr: spanYears >= 0.5 ? cagr(first.awardedRate, last.awardedRate, spanYears) : null,
      avgChangePerTender: changes.length ? geoMean(changes.map((c) => 1 + c)) - 1 : null,

      min: Math.min(...rates),
      max: Math.max(...rates),
      mean: mean(rates),
      median: med,
      p25: percentile(rates, 25),
      p75: percentile(rates, 75),
      spread: Math.max(...rates) - Math.min(...rates),
      volatility: vol,

      trend: fit ? { slope: fit.slope, r2: fit.r2, perYearPct: med > 0 ? fit.slope / med : null } : null,

      suppliers,
      supplierCount: suppliers.length,
      concentration: hhi,
      concentrationLabel: concentrationLabel(hhi, suppliers.length),
      topSupplierShare: suppliers[0]?.share ?? null,

      benchmarkedCount: benchmarked.length,
      belowBenchmarkCount: belowBenchmark.length,
      benchmarkBeatRate: benchmarked.length ? belowBenchmark.length / benchmarked.length : null,
      savingsDeclared: history.reduce((s, r) => s + (r.costSavingAchieved || 0), 0),
      savingsDerived: benchmarked.reduce(
        (s, r) => s + Math.max(0, r.benchmarkRate - r.awardedRate), 0,
      ),

      byYear,
      awards,
      observations: awards
        .filter((a) => a.observation)
        .map((a) => ({ date: a.date, tenderRef: a.tenderRef, supplier: a.supplier, text: a.observation }))
        .reverse(),

      confidence: confidenceOf(history.length, vol),
      trackable: history.length >= 2,
    };
  }).sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
}

/**
 * Products × years matrix of median rate and year-on-year shift.
 * Because the product is fixed down the row, every comparison here is
 * inherently like-for-like — no mix-shift caveat applies.
 */
export function productInflationMatrix(records, { minAwards = 2 } = {}) {
  const products = buildProductIntelligence(records).filter((p) => p.n >= minAwards);
  const years = availableYears(records).slice().reverse();
  return { years, products };
}

/**
 * Products moving fastest in either direction. Ranked on annualised rate where
 * enough time has elapsed to annualise, otherwise on the shift between the two
 * most recent awards, so short-history products still surface.
 */
export function topMovers(records, limit = 6) {
  const products = buildProductIntelligence(records).filter((p) => p.n >= 2);

  const scored = products.map((p) => {
    const usedCagr = p.cagr !== null;
    return {
      ...p,
      moveRate: usedCagr ? p.cagr : p.latestChange,
      moveBasis: usedCagr ? "annualised" : "last award",
    };
  }).filter((p) => p.moveRate !== null && isFinite(p.moveRate));

  const risers = scored
    .filter((p) => p.moveRate > 0.005)
    .sort((a, b) => b.moveRate - a.moveRate)
    .slice(0, limit);

  const fallers = scored
    .filter((p) => p.moveRate < -0.005)
    .sort((a, b) => a.moveRate - b.moveRate)
    .slice(0, limit);

  const volatile = products
    .filter((p) => p.n >= 3 && p.volatility !== null)
    .sort((a, b) => b.volatility - a.volatility)
    .slice(0, limit);

  return { risers, fallers, volatile, all: scored };
}

/**
 * Portfolio-level roll-up of the product dimension, for the summary tiles.
 */
export function productPortfolioStats(records) {
  const products = buildProductIntelligence(records);
  const tracked = products.filter((p) => p.n >= 2);
  const moves = tracked.map((p) => p.cagr).filter((x) => x !== null && isFinite(x));
  const vols = products.map((p) => p.volatility).filter((x) => x !== null);

  return {
    total: products.length,
    tracked: tracked.length,
    singleAward: products.length - tracked.length,
    avgAnnualMove: moves.length ? geoMean(moves.map((m) => 1 + m)) - 1 : null,
    avgVolatility: vols.length ? mean(vols) : null,
    singleSourced: products.filter((p) => p.n >= 2 && p.supplierCount === 1).length,
    competitive: products.filter((p) => p.supplierCount >= 3).length,
    categories: new Set(products.map((p) => p.category)).size,
  };
}

/**
 * Normalised comparison of several products on one axis. Each series is rebased
 * to 100 at its own first award so products priced in different units and
 * currencies can still be read against each other.
 */
export function compareProducts(records, keys) {
  const all = buildProductIntelligence(records);
  return keys
    .map((k) => all.find((p) => p.key === k))
    .filter(Boolean)
    .map((p) => ({
      key: p.key,
      name: p.name,
      category: p.category,
      currency: p.currency,
      unitOfMeasure: p.unitOfMeasure,
      points: p.awards.map((a) => ({
        x: a.t,
        y: a.index,
        tip: `${a.tenderRef}\n${new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}\nIndex ${a.index.toFixed(1)} · ${p.currency} ${a.rate.toLocaleString("en-NG")}`,
      })),
    }));
}

/**
 * Product-level briefing lines, mirroring generateInsights but written for the
 * buyer of a specific line rather than the category manager.
 */
export function generateProductInsights(records, limit = 8) {
  const products = buildProductIntelligence(records);
  const tracked = products.filter((p) => p.n >= 2);
  const out = [];

  if (!tracked.length) {
    return [{
      severity: "info",
      title: "No product has been tendered twice yet",
      body: `${products.length} product line${products.length === 1 ? "" : "s"} recorded. Price shift can only be measured once the same product — same commodity group, unit and currency — is closed out a second time.`,
    }];
  }

  const { risers, fallers, volatile } = topMovers(records, 3);

  risers.slice(0, 3).forEach((p) => {
    out.push({
      severity: p.moveRate > 0.20 ? "critical" : "warn",
      title: `${p.name} up ${fmtPct(p.moveRate)} ${p.moveBasis === "annualised" ? "a year" : "on the last award"}`,
      body: `${fmtMoney(p.firstRate, p.currency, 0)} to ${fmtMoney(p.latestRate, p.currency, 0)} across ${p.n} awards${p.unitOfMeasure ? ` (${p.unitOfMeasure})` : ""}. ${p.supplierCount === 1 ? `All awards went to ${p.suppliers[0].name} — no competitive check on this increase.` : `${p.supplierCount} suppliers have bid on this line; test the increase against the others before renewal.`}`,
    });
  });

  fallers.slice(0, 2).forEach((p) => {
    out.push({
      severity: "good",
      title: `${p.name} down ${fmtPct(Math.abs(p.moveRate))} ${p.moveBasis === "annualised" ? "a year" : "on the last award"}`,
      body: `Now ${fmtMoney(p.latestRate, p.currency, 0)}${p.unitOfMeasure ? ` ${p.unitOfMeasure.toLowerCase()}` : ""}. Keep the contract term short to stay exposed to a softening price rather than locking today's rate in.`,
    });
  });

  volatile.slice(0, 2).forEach((p) => {
    if (p.volatility === null || p.volatility < 0.25) return;
    out.push({
      severity: "warn",
      title: `${p.name} prices swing ±${fmtPct(p.volatility)}`,
      body: `Awards have ranged ${fmtMoney(p.min, p.currency, 0)} to ${fmtMoney(p.max, p.currency, 0)} over ${p.n} tenders. That dispersion is negotiating room — anchor the next exercise near the lower quartile of ${fmtMoney(p.p25, p.currency, 0)}.`,
    });
  });

  const locked = products.filter((p) => p.n >= 3 && p.supplierCount === 1);
  locked.slice(0, 2).forEach((p) => {
    out.push({
      severity: "warn",
      title: `${p.name} has only ever been awarded to ${p.suppliers[0].name}`,
      body: `${p.n} consecutive awards to a single supplier. Without a competing bid the rate cannot be validated — widen the bidder list before the next renewal.`,
    });
  });

  const strong = products.filter((p) => p.benchmarkBeatRate !== null && p.benchmarkBeatRate >= 0.75 && p.benchmarkedCount >= 2);
  if (strong.length) {
    const p = strong.sort((a, b) => b.savingsDerived - a.savingsDerived)[0];
    out.push({
      severity: "good",
      title: `${p.name} consistently beats its benchmark`,
      body: `${p.belowBenchmarkCount} of ${p.benchmarkedCount} awards came in below the benchmark rate, worth ${fmtCompact(p.savingsDerived, p.currency)} in implied saving. Whatever is being done on this line is working — worth repeating elsewhere in ${p.category}.`,
    });
  }

  const noGroup = usableRecords(records).filter((r) => !r.commodityGroup).length;
  if (noGroup > 0) {
    out.push({
      severity: "info",
      title: `${noGroup} award${noGroup === 1 ? "" : "s"} recorded without a commodity group`,
      body: "These fall back to matching on service description, which rarely repeats word for word. Filling in the commodity group is what lets an award join a product's price history.",
    });
  }

  const order = { critical: 0, warn: 1, good: 2, info: 3 };
  return out.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, limit);
}

// ── Formatting helpers (shared with the UI) ──────────────────────────────────

export function fmtPct(x, decimals = 1) {
  if (x === null || x === undefined || !isFinite(x)) return "—";
  return `${(x * 100).toFixed(decimals)}%`;
}

export function fmtSignedPct(x, decimals = 1) {
  if (x === null || x === undefined || !isFinite(x)) return "—";
  return `${x > 0 ? "+" : ""}${(x * 100).toFixed(decimals)}%`;
}

export function fmtMoney(x, currency = "NGN", decimals = 2) {
  if (x === null || x === undefined || !isFinite(x)) return "—";
  return `${currency} ${Number(x).toLocaleString("en-NG", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function fmtCompact(x, currency = "") {
  if (x === null || x === undefined || !isFinite(x)) return "—";
  const abs = Math.abs(x);
  const c = currency ? `${currency} ` : "";
  if (abs >= 1e9) return `${c}${(x / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${c}${(x / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${c}${(x / 1e3).toFixed(1)}K`;
  return `${c}${x.toFixed(0)}`;
}
